/**
 * `ProgressBar` (W1-12 ui-kit-i18n; contract ProgressBarProps): role="progressbar" with a label and a percentage
 * (value 0..1), or an indeterminate bar (value null: no aria-valuenow, animated stripe that stops under reduced
 * motion and is replaced by a static pattern).
 */

import { useId } from 'react';
import { useT } from '../i18n/useT';
import { useTr } from './bridge';
import { cx } from './internal/cx';
import s from './ProgressBar.module.css';
import type { ProgressBarProps } from './types';
import { m } from './ui.messages';

export function ProgressBar(props: ProgressBarProps & { readonly className?: string | undefined }) {
  const t = useT();
  const tr = useTr();
  const labelId = useId();
  const value = props.value === null ? null : Math.min(1, Math.max(0, Number.isFinite(props.value) ? props.value : 0));
  const label = props.label === undefined ? t(m['progress.label']) : tr(props.label);
  const percent = value === null ? null : t(m['progress.percent'], { value });
  return (
    <div className={cx(s.wrap, props.className)}>
      <div className={s.header}>
        <span id={labelId} className={s.label}>
          {label}
        </span>
        {percent !== null && (
          <span className={s.percent} aria-hidden>
            {percent}
          </span>
        )}
      </div>
      <div
        role="progressbar"
        className={cx(s.track, value === null && s.indeterminate)}
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={value === null ? undefined : Math.round(value * 100)}
        aria-valuetext={percent ?? undefined}
        aria-busy={value === null || value < 1 ? true : undefined}
      >
        <div className={s.fill} style={value === null ? undefined : { transform: `scaleX(${String(value)})` }} />
      </div>
    </div>
  );
}
